import React, { useState } from 'react';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const MethodologyConfigurator = () => {
  const [configuration, setConfiguration] = useState({
    organizationSize: '',
    industry: '',
    aiMaturity: '',
    timeline: ''
  });

  const [recommendation, setRecommendation] = useState(null);

  const organizationSizes = [
    { value: 'startup', label: 'Startup (1-50 employees)' },
    { value: 'small', label: 'Small Business (51-200 employees)' },
    { value: 'medium', label: 'Medium Enterprise (201-1000 employees)' },
    { value: 'large', label: 'Large Enterprise (1000+ employees)' }
  ];

  const industries = [
    { value: 'technology', label: 'Technology & Software' },
    { value: 'finance', label: 'Financial Services' },
    { value: 'healthcare', label: 'Healthcare & Life Sciences' },
    { value: 'manufacturing', label: 'Manufacturing' },
    { value: 'retail', label: 'Retail & E-commerce' },
    { value: 'consulting', label: 'Professional Services' },
    { value: 'other', label: 'Other Industry' }
  ];

  const aiMaturityLevels = [
    { value: 'beginner', label: 'AI Beginner - Just starting AI journey' },
    { value: 'exploring', label: 'AI Exploring - Evaluating AI opportunities' },
    { value: 'implementing', label: 'AI Implementing - Pilot projects underway' },
    { value: 'scaling', label: 'AI Scaling - Expanding successful pilots' }
  ];

  const timelines = [
    { value: '3-months', label: '3 months - Rapid deployment' },
    { value: '6-months', label: '6 months - Balanced approach' },
    { value: '12-months', label: '12 months - Comprehensive transformation' },
    { value: 'flexible', label: 'Flexible timeline based on needs' }
  ];

  const generateRecommendation = () => {
    const { organizationSize, industry, aiMaturity, timeline } = configuration;
    
    if (!organizationSize || !industry || !aiMaturity || !timeline) {
      return;
    }

    // Generate customized recommendation based on selections
    const recommendations = {
      phases: [],
      duration: '',
      investment: '',
      keyFocus: [],
      riskFactors: [],
      successProbability: ''
    };

    // Customize based on organization size
    if (organizationSize === 'startup' || organizationSize === 'small') {
      recommendations.phases = ['Assessment', 'Architecture', 'Implementation'];
      recommendations.duration = '3-6 months';
      recommendations.investment = '$50K - $200K';
      recommendations.keyFocus = ['Cost efficiency', 'Rapid deployment', 'Minimal disruption'];
    } else {
      recommendations.phases = ['Assessment', 'Architecture', 'Implementation', 'Integration', 'Optimization'];
      recommendations.duration = '6-12 months';
      recommendations.investment = '$200K - $1M+';
      recommendations.keyFocus = ['Enterprise scalability', 'Security compliance', 'Change management'];
    }

    // Customize based on AI maturity
    if (aiMaturity === 'beginner') {
      recommendations.riskFactors = ['Skills gap', 'Change resistance', 'Technology adoption'];
      recommendations.successProbability = '75%';
    } else if (aiMaturity === 'scaling') {
      recommendations.riskFactors = ['Integration complexity', 'Legacy system compatibility'];
      recommendations.successProbability = '90%';
    } else {
      recommendations.riskFactors = ['Resource allocation', 'Timeline management'];
      recommendations.successProbability = '85%';
    }

    setRecommendation(recommendations);
  };

  const resetConfigurator = () => {
    setConfiguration({
      organizationSize: '',
      industry: '',
      aiMaturity: '',
      timeline: ''
    });
    setRecommendation(null);
  };

  return (
    <div className="bg-white rounded-xl shadow-executive p-8">
      <div className="text-center mb-8">
        <Icon name="Settings" size={48} className="text-brand-primary mx-auto mb-4" />
        <h3 className="text-2xl font-bold text-text-primary mb-2">Methodology Configurator</h3>
        <p className="text-text-secondary">
          Customize the AI Core Blueprint based on your organization's specific needs and context
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <Select
          label="Organization Size"
          placeholder="Select your organization size"
          options={organizationSizes}
          value={configuration?.organizationSize}
          onChange={(value) => setConfiguration(prev => ({ ...prev, organizationSize: value }))}
        />

        <Select
          label="Industry"
          placeholder="Select your industry"
          options={industries}
          value={configuration?.industry}
          onChange={(value) => setConfiguration(prev => ({ ...prev, industry: value }))}
        />

        <Select
          label="AI Maturity Level"
          placeholder="Select your AI maturity"
          options={aiMaturityLevels}
          value={configuration?.aiMaturity}
          onChange={(value) => setConfiguration(prev => ({ ...prev, aiMaturity: value }))}
        />

        <Select
          label="Preferred Timeline"
          placeholder="Select timeline preference"
          options={timelines}
          value={configuration?.timeline}
          onChange={(value) => setConfiguration(prev => ({ ...prev, timeline: value }))}
        />
      </div>
      <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
        <Button
          variant="default"
          onClick={generateRecommendation}
          disabled={!configuration?.organizationSize || !configuration?.industry || !configuration?.aiMaturity || !configuration?.timeline}
          iconName="Zap"
          iconPosition="left"
          className="bg-brand-primary hover:bg-brand-primary/90"
        >
          Generate Custom Blueprint
        </Button>
        
        <Button
          variant="outline"
          onClick={resetConfigurator}
          iconName="RotateCcw"
          iconPosition="left"
        >
          Reset Configuration
        </Button>
      </div>
      {recommendation && (
        <div className="border-t border-gray-200 pt-8">
          <div className="mb-6">
            <h4 className="text-xl font-bold text-text-primary mb-4 flex items-center">
              <Icon name="Target" size={24} className="text-brand-primary mr-2" />
              Your Customized Blueprint
            </h4>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-6 rounded-lg text-center">
              <Icon name="Clock" size={32} className="text-blue-600 mx-auto mb-3" />
              <div className="text-lg font-bold text-blue-900">{recommendation?.duration}</div>
              <div className="text-sm text-blue-700">Estimated Duration</div>
            </div>

            <div className="bg-gradient-to-br from-green-50 to-green-100 p-6 rounded-lg text-center">
              <Icon name="DollarSign" size={32} className="text-green-600 mx-auto mb-3" />
              <div className="text-lg font-bold text-green-900">{recommendation?.investment}</div>
              <div className="text-sm text-green-700">Investment Range</div>
            </div>

            <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-6 rounded-lg text-center">
              <Icon name="Layers" size={32} className="text-purple-600 mx-auto mb-3" />
              <div className="text-lg font-bold text-purple-900">{recommendation?.phases?.length} Phases</div>
              <div className="text-sm text-purple-700">Implementation Phases</div>
            </div>

            <div className="bg-gradient-to-br from-orange-50 to-orange-100 p-6 rounded-lg text-center">
              <Icon name="TrendingUp" size={32} className="text-orange-600 mx-auto mb-3" />
              <div className="text-lg font-bold text-orange-900">{recommendation?.successProbability}</div>
              <div className="text-sm text-orange-700">Success Probability</div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div>
              <h5 className="text-lg font-semibold text-text-primary mb-4">Key Focus Areas</h5>
              <div className="space-y-3">
                {recommendation?.keyFocus?.map((focus, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <Icon name="CheckCircle" size={16} className="text-green-500 flex-shrink-0" />
                    <span className="text-text-secondary">{focus}</span>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h5 className="text-lg font-semibold text-text-primary mb-4">Risk Factors to Consider</h5>
              <div className="space-y-3">
                {recommendation?.riskFactors?.map((risk, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <Icon name="AlertTriangle" size={16} className="text-orange-500 flex-shrink-0" />
                    <span className="text-text-secondary">{risk}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="mt-8 p-6 bg-gradient-to-r from-brand-primary/10 to-brand-secondary/10 rounded-lg">
            <div className="flex items-start space-x-4">
              <Icon name="Lightbulb" size={24} className="text-brand-primary flex-shrink-0 mt-1" />
              <div>
                <h6 className="font-semibold text-text-primary mb-2">Next Steps Recommendation</h6>
                <p className="text-text-secondary text-sm leading-relaxed">
                  Based on your configuration, we recommend starting with a comprehensive AI readiness assessment 
                  followed by a pilot implementation in your highest-impact use case. This approach minimizes risk 
                  while demonstrating clear ROI to stakeholders.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MethodologyConfigurator;